import { TextField } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { makeTestId } from '../../utils/Declarative';
import { findBlurRelatedTarget, selectInputText, shouldIgnoreFocusEvent } from '../../utils/DomUtil';
import { InputAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut, mergeEventHandlers } from '../../utils/ReactHelpers';

export interface TextFieldProps {
  /** NOTE: applied to the root `<div>`, not the `<input>`. */
  className?: string;
  /**
   * * `"outline"` shows a border around the entire field
   * * `"line"` shows a bottom-only border
   * * `"search"` shows as 'outline', but with a search icon
   * @default ```"outline"```
   *
   * NOTE: `"line"` is the default kind according to our DLS.
   * Unfortunately, we cannot correct for this historical mistake due to so many uses depending on the current incorrect default value.
   */
  kind?: 'outline' | 'line' | 'search';
  /** Label to display above the field. */
  label: React.ReactNode;
  /**
   * If provided, the `<label>` element will use this string as its DOM id, rather than the default auto-generated value.
   * Primarily useful for referencing in e.g. `aria-labelledby` on a DOM-distant element.
   */
  labelId?: string;
  /**
   * The contents of the helper message under the field, if any.
   * Set to `<br />` to reserve vertical space for showing a validation message when `valid` is `undefined`.
   */
  message?: React.ReactNode;
  /**
   * The `isComposing` field in the event indicates whether the user is still using an IME, e.g. to input CJK text.
   *
   * When updating `value` in response to a `change` event, it is important to do so regardless of the value of `isComposing`, otherwise
   * IME input will be completely blocked. However, in some cases it may be prudent to e.g. decline to dispatch a server callback until
   * this event fires with `isComposing` set to false.
   *
   * n.b. In addition to native invocations, `TextFieldWidget` will invoke this prop whenever the user transitions out
   * of using an IME. e.g. pressing the key sequence `a, ENTER` in Japanese will trigger two `change` events: the first with
   * `isComposing=true` and the second with `isComposing=false`.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement> & { isComposing: boolean }) => void;
  /**
   * Called when the user indicates to clear the text of the field.
   * The field will not show the clear button to the user unless this prop is provided.
   * */
  onClear?: () => void;
  /**
   * * `undefined` or `null`: the field label will be shown without indication of input requirement.
   * * `false`: the field label will be shown with 'Optional' indication.
   * * `true`: the field label will be shown with 'Required' indication. Additionally setting `valid` to `false` will show the 'Required'
   *   indication with red coloring, e.g. when form submission is attempted without a required value.
   */
  required?: boolean;
  /** When true, the full contents of the field will be selected each time the field is focused. */
  selectOnFocus?: boolean;
  /** Will be suffixed with `'-TextInput'` */
  testId: string;
  /**
   * * `undefined` or `null`: the field will be shown with normal coloring
   * * `"pending"`: the field will be shown with normal coloring and a loading animation
   * * `false`: the field will be shown with red coloring, and `aria-errormessage`/`aria-invalid` will be set on the `<input>`
   * * `true`: the field will be shown with green coloring
   * @default undefined
   */
  valid?: boolean | 'pending';
}

/** A drop-in replacement for an INPUT element with standard styling, accessible label and validation messages, and focus indication. */
export const TextFieldWidget = React.forwardRef<HTMLInputElement, TextFieldProps & Omit<InputAttrs, 'onChange'>>((props, ref) => {
  const { className, onBlur, onChange, selectOnFocus, testId, ...fieldProps } = props;
  const isComposing = React.useRef<boolean>(false);
  const StyleName = TypeStyle.style({ // this has to be done inline as localized messages aren't initialized early enough
    $nest: {
      '&.required label::after': {
        content: `"${Messages.required()}"`,
        marginLeft: 4,
      },
      '&.required.invalid label::after': {
        color: '#c93a47',
        fontWeight: 'bold',
      },
      '&.optional label::after': {
        content: `"${Messages.optional()}"`,
        marginLeft: 4,
      }
    }
  });
  return <TextField
    {...EventUtilsOptOut}
    data-tb-test-id={makeTestId(testId, 'TextInput')}
    {...fieldProps}
    className={TypeStyle.classes(className, StyleName, {
      required: fieldProps.required === true,
      optional: fieldProps.required === false,
    })}
    onBlur={onBlur && (e => {
      e.relatedTarget = findBlurRelatedTarget(e)!; // polyfill for IE11
      onBlur(e);
    })}
    onChange={onChange && (e => onChange({ ...e, isComposing: isComposing.current }))}
    onCompositionEnd={mergeEventHandlers(e => {
      isComposing.current = false;
      onChange?.({ ...(e as unknown as React.ChangeEvent<HTMLInputElement>), isComposing: false });
    }, props.onCompositionEnd)}
    onCompositionStart={mergeEventHandlers(() => isComposing.current = true, props.onCompositionStart)}
    onFocus={e => {
      if (shouldIgnoreFocusEvent(e)) { return; }
      if (selectOnFocus) {
        selectInputText(e.target);
      }
      props.onFocus?.(e);
    }}
    ref={ref}
  />;
});
TextFieldWidget.displayName = 'TextFieldWidget';
TextFieldWidget.defaultProps = { kind: 'outline' };
